/**
 * Route Utilities
 *
 * Simple helpers for route calculations and grouping.
 *
 * DDD Note: Following DDD architect recommendation - NO optimization algorithms.
 * MVP just visualizes routes in scheduled order, doesn't optimize them.
 */

import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';
import type { Coordinates } from './geocoding';

export interface RouteByDriver {
  driverId: string | undefined;
  driverName: string;
  jobs: JobCardViewModel[];
  color: string;
}

/**
 * Group jobs by assigned driver
 *
 * Jobs without a driver go into "Unassigned" group.
 * Jobs are kept in their existing order (by scheduled date).
 *
 * @param jobs - Array of job view models
 * @returns Jobs grouped by driver
 */
export function groupJobsByDriver(
  jobs: JobCardViewModel[]
): RouteByDriver[] {
  const grouped = new Map<string, JobCardViewModel[]>();

  jobs.forEach((job) => {
    const driverId = job.assignedDriver?.id ?? 'unassigned';
    if (!grouped.has(driverId)) {
      grouped.set(driverId, []);
    }
    grouped.get(driverId)!.push(job);
  });

  // Convert to array with metadata
  const routes: RouteByDriver[] = [];
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#14B8A6', // teal
    '#F97316', // orange
  ];

  let colorIndex = 0;

  grouped.forEach((driverJobs, driverId) => {
    const firstJob = driverJobs[0];
    if (!firstJob) return; // Skip empty groups (shouldn't happen but type safety)

    routes.push({
      driverId: driverId === 'unassigned' ? undefined : driverId,
      driverName:
        driverId === 'unassigned'
          ? 'Unassigned'
          : firstJob.assignedDriver?.name ?? 'Unknown Driver',
      jobs: driverJobs,
      color: colors[colorIndex % colors.length] ?? '#6B7280', // Fallback color
    });
    colorIndex++;
  });

  return routes;
}

/**
 * Calculate straight-line distance between two coordinates
 *
 * Uses Haversine formula for great-circle distance.
 * This is NOT driving distance - just as-the-crow-flies.
 *
 * @param from - Starting coordinates
 * @param to - Ending coordinates
 * @returns Distance in miles
 */
export function calculateDistance(
  from: Coordinates,
  to: Coordinates
): number {
  const R = 3959; // Earth's radius in miles

  const lat1 = (from.latitude * Math.PI) / 180;
  const lat2 = (to.latitude * Math.PI) / 180;
  const deltaLat = ((to.latitude - from.latitude) * Math.PI) / 180;
  const deltaLon = ((to.longitude - from.longitude) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Calculate total route distance
 *
 * Sum of distances between consecutive stops.
 * This is straight-line distance, NOT driving distance.
 *
 * @param coordinates - Array of coordinates in route order
 * @returns Total distance in miles
 */
export function calculateRouteDistance(
  coordinates: Coordinates[]
): number {
  if (coordinates.length < 2) {
    return 0;
  }

  let totalDistance = 0;

  for (let i = 0; i < coordinates.length - 1; i++) {
    const current = coordinates[i];
    const next = coordinates[i + 1];
    if (current && next) {
      totalDistance += calculateDistance(current, next);
    }
  }

  return totalDistance;
}

/**
 * Get coordinates from job
 *
 * Handles both persisted coordinates and geocoded coordinates.
 *
 * @param job - Job view model
 * @param geocoded - Map of job IDs to geocoded coordinates
 * @returns Coordinates or null if not available
 */
export function getJobCoordinates(
  job: JobCardViewModel,
  geocoded: Record<string, Coordinates>
): Coordinates | null {
  // Check geocoded first (client-side geocoded addresses)
  const coords = geocoded[job.id];
  if (coords) {
    return coords;
  }

  // Fall back to persisted coordinates (if available in future)
  // For now, JobCardViewModel doesn't expose location.latitude directly
  // This is a placeholder for when backend adds these fields
  return null;
}
